import * as React from "react";
import { CheckIcon, Loader2Icon, PlusIcon, SettingsIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useCreateTagMutation,
  useLazyTagAutocompleteQuery,
  useTagAutocompleteQuery,
} from "../../services/api";
import { useNotificationsProvider } from "../../context/NotificationsContext";

/*
 * Reusable multi-select of Tags backed by the autocomplete RTK Query
 * endpoint. Renders selected tags as coloured chips with an "x" remove
 * affordance, and a debounced typeahead popover for picking new ones.
 *
 * Inline creation: when the typed text matches no existing tag, an extra
 * row "Create '<name>'" appears (GitHub-style). Selecting it creates the
 * tag via `createTag` and adds it to the value immediately. Disabled in
 * `filter` mode and via `allowCreate={false}`.
 */

interface Tag {
  id: number;
  name: string;
  color: string | null;
}

interface AutocompleteOption {
  id: number;
  title: string;
  color: string | null;
}

interface TagsMultiSelectProps {
  value: Tag[];
  onChange: (next: Tag[]) => void;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  mode?: "edit" | "filter";
  /** Inline creation — defaults to `true` (callers can opt out with `allowCreate={false}`). */
  allowCreate?: boolean;
  className?: string;
}

const DEBOUNCE_MS = 250;

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

export function TagsMultiSelect({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  mode = "edit",
  allowCreate,
  className,
}: TagsMultiSelectProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  // Allow creation in both edit and filter modes by default — GitHub-style.
  // Callers can override per call-site with `allowCreate={false}`.
  const creationEnabled = allowCreate ?? true;

  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  // Ref to the input so we can keep the popover open when the user clicks
  // on it: Radix treats anchor clicks as "outside" interactions otherwise,
  // and closes the popover immediately on every focus.
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Debounce the search query — the cache key axis is `q`, so we only
  // want to refetch after the user stops typing.
  React.useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => {
      setDebouncedSearch(inputText);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [open, inputText]);

  const { data, isFetching } = useTagAutocompleteQuery(
    { q: debouncedSearch, limit: 20 },
    { skip: !open },
  ) as { data?: AutocompleteOption[]; isFetching: boolean };

  const [createTag, { isLoading: isCreating }] = useCreateTagMutation();
  // Used to resolve an existing tag by exact name when creation 409s
  // (e.g. the tag exists but is inactive and therefore not in the
  // autocomplete results).
  const [triggerAutocomplete] = useLazyTagAutocompleteQuery();

  const options = (data ?? []).filter(
    (option) => !value.some((tag) => tag.id === option.id),
  );

  const trimmed = inputText.trim();
  const normalizedInput = normalize(trimmed);
  const exactMatchInOptions = options.some(
    (opt) => normalize(opt.title) === normalizedInput,
  );
  const exactMatchInValue = value.some(
    (tag) => normalize(tag.name) === normalizedInput,
  );
  const canShowCreateRow =
    creationEnabled &&
    trimmed.length >= 1 &&
    !exactMatchInOptions &&
    !exactMatchInValue;

  function handleSelect(option: AutocompleteOption) {
    onChange([
      ...value,
      { id: option.id, name: option.title, color: option.color },
    ]);
    setInputText("");
    setDebouncedSearch("");
  }

  async function handleCreate() {
    if (!trimmed || isCreating) return;
    try {
      const created = (await createTag({
        name: trimmed,
        color: null,
      }).unwrap()) as Tag;
      onChange([...value, created]);
      setInputText("");
      setDebouncedSearch("");
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        // Tag exists (possibly inactive) — find it and add it instead.
        try {
          const matches = (await triggerAutocomplete({
            q: trimmed,
            limit: 20,
          }).unwrap()) as AutocompleteOption[];
          const hit = matches.find(
            (opt) => normalize(opt.title) === normalizedInput,
          );
          if (hit) {
            handleSelect(hit);
            return;
          }
        } catch {
          /* fall through to generic error */
        }
        showNotification(
          "error",
          t("tags.errors.name-already-used", "A tag with that name already exists"),
        );
      } else {
        showNotification("error", t("common.api-error"));
      }
    }
  }

  function handleRemove(id: number) {
    onChange(value.filter((tag) => tag.id !== id));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    if (!canShowCreateRow) return;
    e.preventDefault();
    handleCreate();
  }

  // Render decisions are cumulative — we never hide the Create row or the
  // Manage footer just because the autocomplete query is in flight or has
  // errored. The popover always shows whatever is currently actionable:
  //   - the (possibly empty) options list whenever there are matches
  //   - the Create row whenever the user has typed something new
  //   - a fetching spinner only when there is literally nothing else to show
  //   - an empty-state copy only when there is literally nothing else to show
  //   - the Manage tags footer below, always visible (route guard handles auth)
  const hasListContent = options.length > 0 || canShowCreateRow;
  const showSpinner = !hasListContent && isFetching;
  const showEmpty = !hasListContent && !isFetching;

  return (
    <div className={cn(mode === "edit" ? "space-y-1" : "space-y-1", className)}>
      {label ? <Label className="text-sm font-medium">{label}</Label> : null}

      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5">
        {value.map((tag) => (
          <Badge
            key={tag.id}
            style={{
              backgroundColor: tag.color ?? undefined,
              color: tag.color ? "#fff" : undefined,
            }}
            className="gap-1"
          >
            {tag.name}
            {!disabled ? (
              <button
                type="button"
                onClick={() => handleRemove(tag.id)}
                aria-label={t("common.clear", "Clear")}
                className="rounded-full opacity-80 hover:opacity-100"
              >
                <XIcon className="size-3" />
              </button>
            ) : null}
          </Badge>
        ))}

        <Popover open={open} onOpenChange={(o) => (!disabled ? setOpen(o) : undefined)}>
          <PopoverAnchor asChild>
            <input
              ref={inputRef}
              className="min-w-[140px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder={value.length === 0 ? (placeholder ?? t("tags.multi-select.placeholder")) : ""}
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => !disabled && setOpen(true)}
              onClick={() => !disabled && setOpen(true)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
            />
          </PopoverAnchor>
          <PopoverContent
            // shadcn defaults Radix Popover content to `z-50`; the MUI
            // `<Dialog>` wrapping add/edit dialogs uses z-index 1300, so
            // without bumping this the popover renders behind the dialog
            // backdrop and looks empty. The collisionPadding keeps the
            // popover off the dialog edges on small viewports.
            className="z-[1500] w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            collisionPadding={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // Treat the input (which is the PopoverAnchor) as part of the
              // popover, not outside it — otherwise every focus/click on
              // the input would re-trigger the close logic and the popover
              // would flicker open and immediately closed.
              const target = e.target as Node | null;
              if (target && inputRef.current?.contains(target)) {
                e.preventDefault();
              }
            }}
          >
            <div className="p-1">
            {showSpinner ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" />
                {t("common.loading")}
              </div>
            ) : showEmpty ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t("tags.multi-select.no-options")}
              </div>
            ) : (
              <ul role="listbox" className="max-h-64 overflow-auto">
                {options.map((option) => (
                  <li
                    key={option.id}
                    role="option"
                    aria-selected={false}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                  >
                    <CheckIcon className="mr-1 size-4 opacity-0" />
                    <span
                      className="inline-block size-3 rounded-full border border-border"
                      style={{ backgroundColor: option.color ?? "transparent" }}
                      aria-hidden
                    />
                    {option.title}
                  </li>
                ))}
                {canShowCreateRow ? (
                  <li
                    role="option"
                    aria-selected={false}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleCreate();
                    }}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent",
                      options.length > 0 && "mt-1 border-t border-border pt-2",
                      isCreating && "pointer-events-none opacity-60",
                    )}
                  >
                    {isCreating ? (
                      <Loader2Icon className="mr-1 size-4 animate-spin" />
                    ) : (
                      <PlusIcon className="mr-1 size-4 text-primary" />
                    )}
                    <span className="text-muted-foreground">
                      {t("tags.multi-select.create-prefix", "Create")}
                    </span>
                    <span className="font-medium">&ldquo;{trimmed}&rdquo;</span>
                  </li>
                ) : null}
              </ul>
            )}
            </div>
            {/*
             * Manage tags is always visible — actual access to /dashboard/tags
             * is enforced by the route guard. Hiding it client-side based on a
             * permission flag is brittle (e.g. fresh tenants whose migrations
             * have not yet wired `page-tags:root` see the field but no escape
             * hatch). Worst case: user clicks and gets a 403, which is fine.
             */}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
                navigate("/dashboard/tags");
              }}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <SettingsIcon className="size-3.5" />
              {t("tags.multi-select.manage", "Manage tags")}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
